/**
 * QR-3: сессия участника, вошедшего по приглашению В БРАУЗЕРЕ.
 *
 * Зачем отдельно от сессии ЛК: у участника нет учётной записи и нет
 * refresh-cookie — сервер выдаёт ему member-JWT прямо в ответе на join
 * (`POST /msg/invites/join`), а продлевает по живому токену
 * (`POST /msg/members/me/token`). Мешать это с `access_token` владельца
 * нельзя: в одном браузере может быть открыт и ЛК, и чат участника, и
 * перезапись чужого ключа выкинула бы одного из них.
 *
 * 🔴 Токен НЕ кладём в sessionStorage: вкладка закрылась — участник потерял
 * доступ к своей же переписке, а второго join по одноразовой ссылке может не
 * быть. localStorage переживает перезагрузку телефона, и это ровно то
 * поведение, которого ждут от мессенджера.
 *
 * 🔴 did (идентификатор криптоустройства) выбирает КЛИЕНТ и шлёт в join;
 * сервер кладёт его клеймом в member-JWT. Поэтому did хранится вместе с
 * токеном: разъедутся они — устройство в токене и устройство в конвертах
 * перестанут совпадать, и переписка молча умрёт.
 */
/**
 * Ключ хранения экспортируется: другие вкладки следят за ним через событие
 * `storage` — вход другим участником обязан немедленно погасить чат
 * предыдущего в СОСЕДНЕЙ вкладке (ревью Codex р.4), иначе она продолжит жить,
 * держать лидерство и при первом же продлении вернуть свою сессию в
 * хранилище поверх новой.
 */
export const MEMBER_SESSION_KEY = 'msg_member_session';
const KEY = MEMBER_SESSION_KEY;

/**
 * 🔴 Запасное хранение В ПАМЯТИ вкладки. Приватный режим Safari (и «блокировать
 * cookies/данные» в любом браузере) роняет `localStorage.setItem` — а это ровно
 * тот телефон, с которого приглашённый чаще всего и заходит. Без запасного
 * хранения join проходил бы, чат открывался, и первое же обновление страницы
 * выбрасывало участника «войдите по ссылке» — при том, что ссылка одноразовая.
 * В памяти сессия живёт до перезагрузки; вызывающий обязан предупредить
 * человека, что вкладку закрывать нельзя ([saveMemberSession] вернёт false).
 */
let memoryFallback = null;

/**
 * Удавалось ли ЭТОЙ вкладке записать сессию в постоянное хранилище.
 *
 * 🔴 Нужен, чтобы отличить «слот стёрли соседи» от «мы туда никогда и не
 * писали» (ревью Codex р.8): в приватном режиме Safari getItem честно
 * возвращает null, а setItem падает — и пустой слот на продлении выглядел бы
 * как выход в соседней вкладке, гася живой чат человеку, у которого никаких
 * соседних вкладок нет.
 */
let persistedOnce = false;

/** Форма сессии. Половинчатая запись — НЕ сессия (см. [loadMemberSession]). */
function valid(s) {
  return Boolean(s)
    && typeof s.token === 'string' && s.token !== ''
    && Number.isInteger(s.memberId) && s.memberId > 0
    && typeof s.did === 'string' && s.did !== ''
    // необязательные, но если есть — обязаны быть своей формы: повреждённая
    // сессия должна отваливаться СРАЗУ, а не поздним невнятным отказом
    && (s.conversationId === undefined || typeof s.conversationId === 'string')
    && (s.peer === undefined || s.peer === null || typeof s.peer === 'object')
    // WEB-19: СВОЯ подпись. Необязательное поле — сессии, снятые до этого
    // среза, его не имеют и обязаны остаться рабочими.
    && (s.displayName === undefined || s.displayName === null
      || typeof s.displayName === 'string');
}

/** @returns {{token, memberId, did, conversationId, peer}|null} */
export function loadMemberSession(storage = localStorage) {
  let raw;
  try {
    raw = storage.getItem(KEY);
  } catch {
    // хранилище запрещено — работаем тем, что успели удержать в памяти
    return valid(memoryFallback) ? memoryFallback : null;
  }
  if (!raw) return valid(memoryFallback) ? memoryFallback : null;
  try {
    const s = JSON.parse(raw);
    // без любого обязательного поля чат всё равно не поднимется, а «почти
    // сессия» увела бы в невнятный отказ вместо честного «войдите по ссылке»
    if (valid(s)) {
      // 🔴 Слот прочитан из хранилища — значит он существует и он наш (ревью
      // Codex р.9). Без этой отметки вкладка ПОСЛЕ ПЕРЕЗАГРУЗКИ считала бы
      // себя «никогда не писавшей», и исчезновение слота (сосед вышел или
      // вошёл другим участником) выглядело бы как приватный режим — продление
      // вернуло бы старый member-JWT обратно.
      persistedOnce = true;
      return s;
    }
  } catch { /* запись повреждена — ниже попробуем память вкладки */ }
  // Повреждённая запись НЕ должна отменять живую сессию этой вкладки
  // (ревью Codex р.2): человек в чате, а перезагрузка UI выкинула бы его.
  return valid(memoryFallback) ? memoryFallback : null;
}

/**
 * @returns {boolean} true — сессия переживёт перезагрузку; false — она живёт
 * только в памяти вкладки, и человеку нужно об этом сказать.
 */
export function saveMemberSession(session, storage = localStorage) {
  memoryFallback = session;
  try {
    storage.setItem(KEY, JSON.stringify(session));
    persistedOnce = true;
    return true;
  } catch {
    return false;
  }
}

/**
 * Забыть сессию В ПАМЯТИ вкладки, НЕ трогая хранилище (ревью Codex р.8).
 * Слот уже не наш — там либо новый участник, либо пусто, — и удалять запись
 * нельзя; но и держать старый member-JWT в памяти незачем: повторный заход на
 * страницу чата в той же вкладке воскресил бы отобранную сессию.
 */
export function forgetMemberSession() {
  memoryFallback = null;
  persistedOnce = false;
}

/**
 * Сохранение ПРОДЛЁННОГО токена — единственный путь для живого чата.
 *
 * 🔴 Слот проверяется на владельца (ревью Codex р.6). Событие `storage`
 * доставляется в соседнюю вкладку АСИНХРОННО, поэтому возможен порядок:
 * вкладка B записала нового участника → продление в вкладке A завершилось
 * раньше, чем до неё дошло событие → слепая запись вернула бы туда СТАРУЮ
 * сессию, отобрав чат у нового участника (ссылка одноразовая — это потеря
 * доступа навсегда). Пустой слот — тот же случай: соседняя вкладка вышла.
 *
 * @returns {'saved'|'fragile'|'stale'} stale — слот занят кем-то другим или
 *   очищен: запись пропущена, вкладка догорит до своего storage-события.
 */
export function saveRefreshedSession(session, storage = localStorage) {
  let raw = null;
  let readable = true;
  // Лучшая известная копия записи: сохранённая, если она есть и наша.
  let base = null;
  try {
    raw = storage.getItem(KEY);
  } catch {
    readable = false; // хранилище запрещено — живём памятью вкладки
  }
  if (readable) {
    if (raw === null) {
      // Слот пуст. Если мы туда УЖЕ писали — значит его стёрли соседи (выход
      // или замена участника). Если не писали никогда — это приватный режим,
      // и чат обязан продолжать работать.
      if (persistedOnce) {
        memoryFallback = session;
        return 'stale';
      }
    } else {
      const owner = parseMemberSession(raw);
      if (owner && owner.memberId !== session.memberId) {
        memoryFallback = session; // вкладка ещё работает, но в хранилище не лезем
        return 'stale';
      }
      // owner === null — в слоте мусор, а не чужая живая сессия: перезапишем
      // своей, человека из живого чата за это не выкидываем (ревью р.2).
      base = owner;
    }
  }
  // 🔴 У продления своё поле — ТОКЕН, и только его оно вправе менять. Записывая
  // сессию целиком, оно возвращало бы в слот свою копию ПОДПИСИ, а та
  // устаревает сразу после переименования: транспорт про смену имени не знает.
  // Человек исправлял бы подпись, а первое же продление откатывало её обратно
  // (ревью Codex р.6). Сливаем: сохранённая копия главнее во всём, кроме
  // токена; недостающие поля добираем из своей.
  // 🔴 Слияние ОБЯЗАНО работать и там, где слота нет: приватный режим и мусор
  // в слоте — те же люди с тем же переименованием, и лучшая известная копия
  // для них живёт в памяти вкладки (ревью Codex р.7).
  if (!base && memoryFallback && memoryFallback.memberId === session.memberId) {
    base = memoryFallback;
  }
  const next = base ? { ...session, ...base, token: session.token } : session;
  return saveMemberSession(next, storage) ? 'saved' : 'fragile';
}

/**
 * WEB-19: сменить ТОЛЬКО подпись, ничего больше.
 *
 * 🔴 Почему отдельной операцией, а не `saveRefreshedSession({...сессия, имя})`.
 * Токен живёт в трёх копиях — состояние страницы, `current` транспорта и эта
 * запись, — и они расходятся по построению: продление намеренно не трогает
 * состояние. Собирая сессию целиком, вызывающий обязан выбрать, чей токен
 * писать, — и три раунда ревью подряд нашли три разных края этого выбора
 * (снимок отстал; хранилище отстало навсегда; ремоунт сбросил признак).
 * Здесь выбора НЕТ: подпись ложится поверх той сессии, что уже лежит в слоте,
 * с её собственным токеном. Класс дефекта закрыт по построению, а не флагом.
 *
 * Имя обновляется в ОБЕИХ копиях, если они есть: в постоянном слоте и в
 * запасной памяти вкладки — иначе после перезагрузки в приватном режиме
 * человек увидел бы старую подпись.
 *
 * @param memberId чей слот правим: чужой не трогаем (см. saveRefreshedSession).
 * @returns {'saved'|'fragile'|'stale'} stale — слот занят другим участником
 *   или очищен: правка пропущена, вкладка догорит до своего storage-события.
 */
export function renameStoredSession(displayName, memberId, storage = localStorage) {
  if (memoryFallback && memoryFallback.memberId === memberId) {
    memoryFallback = { ...memoryFallback, displayName };
  }
  // 🔴 'stale' здесь означает РОВНО ОДНО: слот принадлежит другому
  // участнику или его стёрли. Только на этот случай вызывающему можно гасить
  // вкладку. Всё остальное — «сохранить не удалось», то есть 'fragile': живой
  // чат из-за неудачной записи ИМЕНИ выкидывать нельзя (ревью Codex р.5).
  let raw = null;
  try {
    raw = storage.getItem(KEY);
  } catch {
    // Хранилище запрещено (приватный режим): имя живёт в памяти вкладки, и
    // перезагрузка потеряет его вместе с сессией — это и есть 'fragile'.
    return 'fragile';
  }
  if (raw === null) {
    // Слот пуст. `persistedOnce` отличает «стёрли соседи» (мы туда писали, и
    // записи не стало) от приватного режима, где её там никогда и не было.
    return persistedOnce ? 'stale' : 'fragile';
  }
  const owner = parseMemberSession(raw);
  if (owner && owner.memberId !== memberId) return 'stale';
  // owner === null — в слоте МУСОР, а не чужая живая сессия. Восстановить
  // сессию отсюда нечем (токена в мусоре нет), но и гасить живой чат не за
  // что: пишем из запасной памяти, если она наша, иначе честный 'fragile'.
  const next = owner
    ? { ...owner, displayName }
    : (memoryFallback && memoryFallback.memberId === memberId ? memoryFallback : null);
  if (!next) return 'fragile';
  try {
    storage.setItem(KEY, JSON.stringify(next));
    persistedOnce = true;
    return 'saved';
  } catch {
    return 'fragile';
  }
}

/**
 * Лежит ли сессия в постоянном хранилище — то есть переживёт ли она
 * перезагрузку страницы.
 *
 * 🔴 Проверяем ФАКТ записи, а не пробную запись «1» (ревью Codex р.2):
 * крошечная проба проходит и там, где настоящая сессия не влезла по квоте,
 * и участник получил бы обещание «чат сохранён», потеряв его при обновлении.
 */
export function sessionIsPersisted(storage = localStorage) {
  try {
    const raw = storage.getItem(KEY);
    return Boolean(raw) && valid(JSON.parse(raw));
  } catch {
    return false;
  }
}

/**
 * Разбор ЗНАЧЕНИЯ хранилища — например, `newValue` события `storage` из
 * соседней вкладки.
 *
 * 🔴 Отдельно от [loadMemberSession] (ревью Codex р.5): та нарочно
 * подмешивает запасную сессию из памяти ЭТОЙ вкладки, и удаление ключа
 * соседней вкладкой выглядело бы как «ничего не изменилось» — вкладка
 * осталась бы жить с мёртвым участником, держать лидерство и однажды
 * вернуть свою сессию в хранилище поверх новой.
 */
export function parseMemberSession(raw) {
  if (!raw) return null;
  try {
    const s = JSON.parse(raw);
    return valid(s) ? s : null;
  } catch {
    return null;
  }
}

export function clearMemberSession(storage = localStorage) {
  memoryFallback = null;
  persistedOnce = false;
  try {
    storage.removeItem(KEY);
  } catch { /* нечего чистить */ }
}

/**
 * Сессия из ответа join — единственное место, где она рождается.
 * @param did тот самый device_id, который УШЁЛ в запрос: сервер положил его
 *            клеймом в токен, ответ его не дублирует.
 * @param displayName подпись, которую человек ввёл при входе. WEB-19: ответ
 *            join её тоже не возвращает, а знать её надо ВЕЗДЕ, а не только в
 *            комнате — иначе «изменить свою подпись» существует лишь на
 *            вкладке комнаты, и единственной заметной дверью к смене имени
 *            остаётся ссылка-приглашение (а она заводит нового участника).
 */
export function sessionFromJoin(join, did, displayName = null) {
  return {
    token: join.access_token,
    memberId: join.member_id,
    did,
    displayName: displayName ?? null,
    conversationId: join.conversation_id,
    peer: { type: join.peer_type, id: join.peer_id, name: join.peer_name ?? null },
  };
}
